import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import coverImage from "@greeter-venue/assets/coverimage.jpg";
import { BookingListItem } from "@greeter-venue/components/BookingListItem";
import { usePersistentStore } from "@greeter/store";
import {
  IonContent,
  IonFab,
  IonFabButton,
  IonIcon,
  IonModal,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  useIonViewDidEnter,
} from "@ionic/react";
import { EmptyOrderlistDefaultMessage } from "@greeter-venue/components/EmptyOrderlistDefaultMessage";
import {
  Booking,
  AllBookingStatus,
  Customer,
  Venue,
  BookingStatus,
  BookingStatusSchema,
} from "@greeter/core";
import { doNothing } from "@greeter/util";
import { RejectModal } from "@greeter-venue/components/RejectModal";
import {
  Toolbar,
  RoundedButton,
  useToggle,
  useAuth,
  LazyCoverImage,
  PageCover,
  PageContainer,
  Carousel,
} from "@greeter/matter";

import { uniq } from "lodash";
import {
  DatePeriod,
  resetTimeOfDay,
  TimeOfDayPeriod,
  withTimeOfDay,
} from "@greeter/date";
import { isSameDay, secondsToHours, subHours } from "date-fns";
import {
  addDays,
  addYears,
  differenceInSeconds,
  isSameYear,
  nextMonday,
  previousMonday,
  subYears,
} from "date-fns";
import { addOutline, chevronDownCircleOutline, options } from "ionicons/icons";
import { classNames } from "@greeter/util";
import { Liquid } from "liquidjs";
import { useHistory } from "react-router-dom";
import { Routes } from "@greeter-venue/utility/Routes";
import * as z from "zod";
import { logger } from "@greeter/log";
import css from "./BookingOverviewPage.module.scss";

const log = logger("[BookingOverviewPage]");
const liquid = new Liquid();

const dateFormatter = Intl.DateTimeFormat("da-DK", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

const yearFormatter = Intl.DateTimeFormat("da-DK", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

// TODO: We've lost typing here. For now it's dynamic.
const AllBookingStatusWithBookingStatus = [
  ...AllBookingStatus,
  "venue.pending-payment",
] as const;
type AllBookingStatusWithBookingStatus =
  (typeof AllBookingStatusWithBookingStatus)[number];

type OptionalAsync<T> = Promise<T> | T;

// TODO: Are these really observer methods?
type OnBooking = (booking: Booking) => OptionalAsync<Booking>;
type OnAcceptBooking = OnBooking;
type OnRejectBooking = (
  booking: Booking,
  rejectionMessage: string
) => OptionalAsync<Booking>;

export type BookingFilter =
  | "today"
  | "tomorrow"
  | "this week"
  | "all upcoming"
  | "all previous";

// TODO: turn this into a setting
const dayPeriod = TimeOfDayPeriod.fromString("08:00", "08:00");
/**
 * This is used to provide an offset for the days
 */
const offsetHours = secondsToHours(dayPeriod.from.getTotalSeconds());

type BookingPeriodFilters = {
  [Filter in BookingFilter]: DatePeriod;
};
export function bookingPeriods(): BookingPeriodFilters {
  const now = new Date();

  return {
    "all upcoming": new DatePeriod({
      from: now,
      to: addYears(resetTimeOfDay(nextMonday(now)), 10),
    }),
    "all previous": new DatePeriod({
      from: subYears(resetTimeOfDay(nextMonday(now)), 10),
      to: now,
    }),
    today: new DatePeriod({
      from: withTimeOfDay(subHours(now, offsetHours), dayPeriod.from),
      to: withTimeOfDay(addDays(subHours(now, offsetHours), 1), dayPeriod.to),
    }),
    "this week": new DatePeriod({
      from: resetTimeOfDay(previousMonday(now)),
      to: resetTimeOfDay(nextMonday(now)),
    }),
    tomorrow: new DatePeriod({
      from: withTimeOfDay(
        addDays(subHours(now, offsetHours), 1),
        dayPeriod.from
      ),
      to: withTimeOfDay(addDays(subHours(now, offsetHours), 2), dayPeriod.to),
    }),
  };
}

function hasBookingStatus(statuses: BookingStatus[], hasStatus: BookingStatus) {
  return !!statuses.find((s) => s === hasStatus);
}

function addBookingStatus(statuses: BookingStatus[], status: BookingStatus) {
  statuses.push(status);
  return uniq(statuses);
}

function removeBookingStatus(statuses: BookingStatus[], status: BookingStatus) {
  return statuses.filter((s) => s !== status);
}

function toggleBookingStatus(
  statuses: BookingStatus[],
  status: BookingStatus
): BookingStatus[] {
  return hasBookingStatus(statuses, status)
    ? removeBookingStatus(statuses, status)
    : addBookingStatus(statuses, status);
}

type SettingsButtonProps = {
  onClick: () => void;
};
function SettingsButton(props: SettingsButtonProps) {
  return (
    <div
      style={{
        borderRadius: "100vw",
        width: "3rem",
        height: "3rem",
        backgroundColor: "rgba(var(--gm-color-default-bg-rgb-value), 0.6)",
        backdropFilter: "blur(5px)",
        border: "2px var(--gm-color-alt-bg) solid",
        zIndex: 100,
        display: "grid",
        placeItems: "center",
      }}
      onClick={props.onClick}
    >
      <IonIcon icon={options} />
    </div>
  );
}

const BookingOverviewSettings = z.object({
  statusFilters: z.array(BookingStatusSchema),
});
type BookingOverviewSettings = z.infer<typeof BookingOverviewSettings>;

export type BookingOverviewPageProps = {
  bookings: Booking[];
  customers: Customer[];
  onAccept?: OnAcceptBooking;
  onReject?: OnRejectBooking;
  onBegin?: OnBooking;
  onEnd?: OnBooking;
  onReachedEnd?: () => void;
  onRefresh?: () => void;
  onFilterChange?: (filter: BookingFilter) => void;
  hasMore?: boolean;
  period?: DatePeriod;
  venue?: Venue;
  venueAdminName?: string;
};

export function BookingOverviewPage({
  bookings = [],
  customers = [],
  onAccept,
  onReject,
  onBegin,
  onEnd,
  onReachedEnd = doNothing,
  onRefresh = doNothing,
  onFilterChange = doNothing,
  hasMore = true,
  period,
  venue,
  venueAdminName,
}: BookingOverviewPageProps) {
  const [isRejectModalToggled, toggleRejectModal] = useToggle(false);
  const [bookingToReject, setBookingToReject] = useState<Booking>();
  const [filter, setFilter] = useState<BookingFilter>("all upcoming");
  const [lastBroadcastFilter, setLastBroadcastFilter] =
    useState<BookingFilter>(filter);

  const [sortedBookings, setSortedBookings] =
    useState<Array<Booking>>(bookings);

  useEffect(() => {
    if (filter === "all previous") {
      setSortedBookings(
        bookings.toSorted((a, b) =>
          differenceInSeconds(b.period.from, a.period.from)
        )
      );
    } else {
      setSortedBookings(
        bookings.toSorted((a, b) =>
          differenceInSeconds(a.period.from, b.period.from)
        )
      );
    }
  }, [bookings, filter]);

  const handleReject = useCallback(
    (booking: Booking) => {
      setBookingToReject(booking);
      toggleRejectModal();
    },
    [toggleRejectModal]
  );

  const bookingsLength = bookings.length;
  const isEmpty = bookingsLength === 0;

  useEffect(() => {
    onFilterChange(filter);
  }, [onFilterChange, filter]);

  useEffect(() => {
    if (filter && lastBroadcastFilter !== filter) {
      onFilterChange(filter);
      setLastBroadcastFilter(filter);
    }
  }, [filter, onFilterChange, lastBroadcastFilter]);

  const contentRef = useRef<HTMLIonContentElement>(null);

  const [atTop] = useState<boolean>(true);

  const history = useHistory();

  const handleRefresh = useCallback(
    async (ev: any) => {
      await onRefresh();
      ev.target.complete();
    },
    [onRefresh]
  );

  const [isFilterSettingsModalOpen, setIsFilterSettingsModalOpen] =
    useState(false);
  const openFilterSettingsModal = useCallback(() => {
    setIsFilterSettingsModalOpen(true);
  }, []);

  const store = usePersistentStore();
  const excludeStaleCreatedMigrationKey =
    "greeter.bookingOverviewSettings.migrations.exclude-stale-created";
  const bookingOverviewSettingsKey = "greeter.bookingOverviewSettings";
  const [bookingOverviewSettings, setBookingOverviewSettings] =
    useState<BookingOverviewSettings>();

  useIonViewDidEnter(() => {
    const json = store.get(bookingOverviewSettingsKey);
    const result = BookingOverviewSettings.safeParse(json);
    const empty: BookingOverviewSettings = {
      statusFilters: [...AllBookingStatusWithBookingStatus].filter(
        (s) => s !== "created" && s !== "stale"
      ),
    };
    if (!result.success) {
      log(
        `Probably no settings set in persistent storage. Parsed with error ${result.error}. Creating a new settings object`
      );
      setBookingOverviewSettings(empty);
      store.save(bookingOverviewSettingsKey, empty);
      store.save(excludeStaleCreatedMigrationKey, "true");
    } else {
      if (
        store.getOrDefault(excludeStaleCreatedMigrationKey, "false") === "true"
      ) {
        setBookingOverviewSettings(empty);
        store.save(bookingOverviewSettingsKey, empty);
        store.save(excludeStaleCreatedMigrationKey, "true");
      } else {
        setBookingOverviewSettings(result.data);
        store.save(excludeStaleCreatedMigrationKey, "true");
      }
    }
  }, []);

  useEffect(() => {
    // Save the settings on change
    if (bookingOverviewSettings)
      store.save(bookingOverviewSettingsKey, bookingOverviewSettings);
  }, [bookingOverviewSettings]);

  const pendingBookings = sortedBookings.filter((b) => b.status === "pending");

  const wrappedBookingListItems = useMemo(
    () => (
      <>
        {pendingBookings.length > 0 && (
          <div
            style={{
              border: "dashed 2px white",
              borderRadius: 10,
              padding: 10,
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
            }}
          >
            <p style={{ fontWeight: "bold" }}>Mangler handling</p>
            {pendingBookings.map((booking) => {
              return (
                <BookingListItem
                  key={booking.id}
                  onAccept={(b) => {
                    if (onAccept) onAccept(b);
                  }}
                  onReject={handleReject}
                  onMarkAsArrived={(b) => {
                    if (onBegin) onBegin(b);
                  }}
                  onEnd={(b) => {
                    if (onEnd) onEnd(b);
                  }}
                  booking={booking}
                  customer={customers.find((c) => c.id === booking.customerId)}
                />
              );
            })}
          </div>
        )}
        {sortedBookings
          .filter((b) => {
            const result = hasBookingStatus(
              bookingOverviewSettings?.statusFilters ?? [],
              b.status
            );
            return result;
          })
          // We're sort of turning accepted into "missing payment"
          .filter((b) => {
            return b.status === "accepted" && b.createdBy === "venueAdmin"
              ? hasBookingStatus(
                  bookingOverviewSettings?.statusFilters ?? [],
                  "venue.pending-payment"
                )
              : true;
          })
          .map((booking) => {
            return (
              <BookingListItem
                key={booking.id}
                onAccept={(b) => {
                  if (onAccept) onAccept(b);
                }}
                onReject={handleReject}
                onMarkAsArrived={(b) => {
                  if (onBegin) onBegin(b);
                }}
                onEnd={(b) => {
                  if (onEnd) onEnd(b);
                }}
                booking={booking}
                customer={customers.find((c) => c.id === booking.customerId)}
              />
            );
          })}
      </>
    ),
    [
      onAccept,
      onBegin,
      sortedBookings,
      bookingOverviewSettings,
      toggleRejectModal,
      customers,
      onEnd,
      handleReject,
    ]
  );

  return (
    <IonPage className={css.Page}>
      <Toolbar
        rightSlot={
          <div style={{ display: "flex", flexDirection: "row", gap: "0.5rem" }}>
            <SettingsButton onClick={openFilterSettingsModal} />
          </div>
        }
      />
      <IonContent
        ref={contentRef}
        {...classNames(css.ContentContainer, css.InfiniteScroll)}
      >
        <PageCover>
          <LazyCoverImage src={coverImage} fade />
        </PageCover>
        <IonFab horizontal="end" vertical="bottom" slot="fixed">
          <IonFabButton
            id="fab-create-booking"
            size="small"
            onClick={() =>
              history.push(
                Routes.mainGroup.editBooking.route({ mode: "create" })
              )
            }
          >
            <IonIcon icon={addOutline} />
          </IonFabButton>
        </IonFab>
        <IonRefresher
          slot="fixed"
          disabled={!atTop}
          onIonRefresh={handleRefresh}
        >
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            refreshingSpinner="circles"
            refreshingText="Genopfrisker bookinger"
          />
        </IonRefresher>
        <PageContainer>
          <div
            className={css.InfiniteScrollWrapper}
            style={{ paddingTop: "env(safe-area-inset-top)" }}
          >
            <div style={{ textAlign: "left" }} className={css.Section}>
              <h2>Bookinger</h2>
              <Carousel
                style={
                  {
                    "--padding-left": "0",
                    "--padding-right": 0,
                  } as React.CSSProperties
                }
              >
                <RoundedButton
                  key="all previous"
                  className={css.FilterButton}
                  activeClassName={css.Active}
                  active={filter === "all previous"}
                  onClick={() => setFilter("all previous")}
                >
                  Tidligere
                </RoundedButton>
                <RoundedButton
                  key="all upcoming"
                  className={css.FilterButton}
                  activeClassName={css.Active}
                  active={filter === "all upcoming"}
                  onClick={() => setFilter("all upcoming")}
                >
                  Fremtidige
                </RoundedButton>
                <RoundedButton
                  key="today"
                  className={css.FilterButton}
                  activeClassName={css.Active}
                  active={filter === "today"}
                  onClick={() => setFilter("today")}
                >
                  I dag
                </RoundedButton>
                <RoundedButton
                  key="tomorrow"
                  className={css.FilterButton}
                  activeClassName={css.Active}
                  active={filter === "tomorrow"}
                  onClick={() => setFilter("tomorrow")}
                >
                  I morgen
                </RoundedButton>
                <RoundedButton
                  key="this week"
                  className={css.FilterButton}
                  activeClassName={css.Active}
                  active={filter === "this week"}
                  onClick={() => setFilter("this week")}
                >
                  Denne uge
                </RoundedButton>
              </Carousel>
            </div>
            {period && isSameDay(period.from, period.to) ? (
              <div>{dateFormatter.format(period.from)}</div>
            ) : period && !isSameYear(period.from, period.to) ? (
              <div>
                {yearFormatter.format(period.from)} -{" "}
                {yearFormatter.format(period.to)}
              </div>
            ) : period ? (
              <div>
                {dateFormatter.format(period.from)} -{" "}
                {dateFormatter.format(period.to)}
              </div>
            ) : (
              <></>
            )}
            {isEmpty ? (
              <EmptyOrderlistDefaultMessage />
            ) : (
              wrappedBookingListItems
            )}
          </div>
          <RejectModal
            isOpen={isRejectModalToggled}
            onReject={async (message) => {
              if (bookingToReject && onReject)
                await onReject(bookingToReject, message);
            }}
            onClose={toggleRejectModal}
            template={async (m) =>
              await liquid.parseAndRender(m, {
                customer: customers.find(
                  (c) => c.id === bookingToReject?.customerId
                )?.name,
                venueAdmin: venueAdminName,
                venue: venue?.name,
              })
            }
          />
        </PageContainer>
        <IonModal
          isOpen={isFilterSettingsModalOpen}
          onIonModalDidDismiss={() => setIsFilterSettingsModalOpen(false)}
          initialBreakpoint={0.75}
          breakpoints={[0, 0.75]}
        >
          <IonContent>
            <div style={{ padding: "1rem" }}>
              <div style={{ paddingBottom: "1rem" }}>
                <h2>Booking status filtre</h2>
                <span>
                  <i>Hvilke status vil du gerne have i din oversigt?</i>
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                }}
              >
                <RoundedButton
                  className={css.FilterButton}
                  activeClassName={css.Active}
                  style={{ width: "fit-content" }}
                  active={hasBookingStatus(
                    bookingOverviewSettings?.statusFilters ?? [],
                    "rejected"
                  )}
                  onClick={() =>
                    setBookingOverviewSettings((s) => ({
                      ...s,
                      statusFilters: toggleBookingStatus(
                        bookingOverviewSettings?.statusFilters ?? [],
                        "rejected"
                      ),
                    }))
                  }
                >
                  Afvist
                </RoundedButton>
                <RoundedButton
                  className={css.FilterButton}
                  activeClassName={css.Active}
                  style={{ width: "fit-content" }}
                  active={hasBookingStatus(
                    bookingOverviewSettings?.statusFilters ?? [],
                    "created"
                  )}
                  onClick={() =>
                    setBookingOverviewSettings((s) => ({
                      ...s,
                      statusFilters: toggleBookingStatus(
                        bookingOverviewSettings?.statusFilters ?? [],
                        "created"
                      ),
                    }))
                  }
                >
                  Afventer betaling
                </RoundedButton>
                <RoundedButton
                  className={css.FilterButton}
                  activeClassName={css.Active}
                  style={{ width: "fit-content" }}
                  active={hasBookingStatus(
                    bookingOverviewSettings?.statusFilters ?? [],
                    "pending"
                  )}
                  onClick={() =>
                    setBookingOverviewSettings((s) => ({
                      ...s,
                      statusFilters: toggleBookingStatus(
                        bookingOverviewSettings?.statusFilters ?? [],
                        "pending"
                      ),
                    }))
                  }
                >
                  Ny booking! 🥳
                </RoundedButton>
                <RoundedButton
                  className={css.FilterButton}
                  activeClassName={css.Active}
                  style={{ width: "fit-content" }}
                  active={hasBookingStatus(
                    bookingOverviewSettings?.statusFilters ?? [],
                    "accepted"
                  )}
                  onClick={() =>
                    setBookingOverviewSettings((s) => ({
                      ...s,
                      statusFilters: toggleBookingStatus(
                        bookingOverviewSettings?.statusFilters ?? [],
                        "accepted"
                      ),
                    }))
                  }
                >
                  Godkendt
                </RoundedButton>
                <RoundedButton
                  className={css.FilterButton}
                  activeClassName={css.Active}
                  style={{ width: "fit-content" }}
                  active={hasBookingStatus(
                    bookingOverviewSettings?.statusFilters ?? [],
                    "inProgress"
                  )}
                  onClick={() =>
                    setBookingOverviewSettings((s) => ({
                      ...s,
                      statusFilters: toggleBookingStatus(
                        bookingOverviewSettings?.statusFilters ?? [],
                        "inProgress"
                      ),
                    }))
                  }
                >
                  Ankommet
                </RoundedButton>
                <RoundedButton
                  className={css.FilterButton}
                  activeClassName={css.Active}
                  style={{ width: "fit-content" }}
                  active={hasBookingStatus(
                    bookingOverviewSettings?.statusFilters ?? [],
                    "completed"
                  )}
                  onClick={() =>
                    setBookingOverviewSettings((s) => ({
                      ...s,
                      statusFilters: toggleBookingStatus(
                        bookingOverviewSettings?.statusFilters ?? [],
                        "completed"
                      ),
                    }))
                  }
                >
                  Afsluttet
                </RoundedButton>
                <RoundedButton
                  className={css.FilterButton}
                  activeClassName={css.Active}
                  style={{ width: "fit-content" }}
                  active={hasBookingStatus(
                    bookingOverviewSettings?.statusFilters ?? [],
                    "venue.pending-payment"
                  )}
                  onClick={() =>
                    setBookingOverviewSettings((s) => ({
                      ...s,
                      statusFilters: toggleBookingStatus(
                        bookingOverviewSettings?.statusFilters ?? [],
                        "venue.pending-payment"
                      ),
                    }))
                  }
                >
                  Mangler betaling
                </RoundedButton>
              </div>
            </div>
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
}
